import { useRef, useState, type MouseEvent } from "react";
import type { MarketplaceEntry } from "../types/index";
import { OfficialCheck } from "./OfficialBadge";
import NewBadge from "./NewBadge";
import SharedUsageNote from "./SharedUsageNote";
import { useIsOverflowing } from "./useIsOverflowing";
import { detailHref } from "../lib/catalog-context";
import { onSpaLinkClick } from "../lib/link-click";
import styles from "./PluginListView.module.css";

const EXTENSION_TYPE_CLASS: Record<string, string> = {
  plugin: "typeBadgePlugin",
  skill: "typeBadgeSkill",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// installName is optional (legacy/malformed records only — every record the catalog
// builder emits today sets it, including synthesized per-skill entries). displayName is
// the fallback rather than `${source}--${orgOrOwner}--${repoName}`: that composite can't
// distinguish two manifests in one repo (a documented, real shape — see CLAUDE.md's
// multi-manifest-monorepo note), so a collision there would silently share this row's
// expand/collapse state with an unrelated row. displayName is a required field and is
// documented project-wide as "unique by construction" ("Plugin Name (org/repo)"), so it
// carries no new uniqueness assumption beyond one the system already guarantees.
function getRowKey(plugin: MarketplaceEntry): string {
  return plugin.installName ?? plugin.displayName;
}

interface DescriptionCellProps {
  description: string | undefined;
  // displayName ("Plugin Name (org/repo)"), not the bare pluginName — pluginName isn't
  // guaranteed unique (only displayName is, by construction; see getRowKey's comment
  // above), so two plugins in different repos sharing a pluginName would otherwise
  // render two toggles with the identical accessible name, the exact ambiguity this
  // aria-label exists to fix.
  displayName: string;
  descriptionId: string;
  isExpanded: boolean;
  onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Its own component (not inlined in the row map below) because it needs a ref +
 * useIsOverflowing, and a hook can't be called a variable number of times inside
 * plugins.map() — each row needs its own stable hook call, which means its own
 * component instance.
 *
 * A char-count heuristic was tried first and reverted: .descriptionCell has no
 * enforced min-width, only max-width: 400px, and the app shell has no responsive
 * breakpoints protecting the table — measured at a 900px viewport, the column was
 * only ~244px wide. A threshold calibrated for 400px under-detects overflow at
 * that width, clamping real text with no toggle to reveal it — worse than this
 * PR's starting point, where the description just wrapped to as many lines as it
 * needed. Real measurement (scrollHeight > clientHeight, re-measured via
 * ResizeObserver) is correct at any column width, which a static number cannot be.
 */
function DescriptionCell({ description, displayName, descriptionId, isExpanded, onToggle }: DescriptionCellProps) {
  const spanRef = useRef<HTMLSpanElement>(null);
  // Measured against the CURRENT className (clamped vs. full) — once expanded,
  // the full text no longer overflows its own unbounded box, so this alone would
  // go false right when the user expands. `isExpanded` is OR'd in below so the
  // toggle (now serving as the collapse control) doesn't disappear on expand.
  const isOverflowingWhileClamped = useIsOverflowing(spanRef);

  return (
    <td className={styles.descriptionCell}>
      <span
        ref={spanRef}
        id={descriptionId}
        className={isExpanded ? styles.descriptionFull : styles.descriptionClamped}
      >
        {description || "No description available"}
      </span>
      {/* aria-label names the row (every toggle otherwise shares the literal text
          "Show more"/"Show less", indistinguishable to assistive tech); aria-controls
          pairs it to the region it discloses, per the WAI-ARIA disclosure pattern. */}
      {(isOverflowingWhileClamped || isExpanded) && (
        <button
          type="button"
          className={styles.expandToggle}
          aria-expanded={isExpanded}
          aria-controls={descriptionId}
          aria-label={`${isExpanded ? "Show less" : "Show more"} of ${displayName} description`}
          onClick={onToggle}
        >
          {isExpanded ? "Show less" : "Show more"}
        </button>
      )}
    </td>
  );
}

interface PluginListViewProps {
  plugins: MarketplaceEntry[];
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
}

export default function PluginListView({ plugins, onSelectPlugin }: PluginListViewProps) {
  // Per-row description expand/collapse. A description is clamped to 2 lines by default
  // (see PluginListView.module.css .descriptionClamped) so a short description doesn't
  // sit inside dead vertical space — a long, deliberately-written description is free to
  // push its own row taller when expanded; this set is how a row opts out of the clamp.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  function toggleExpanded(key: string, event: MouseEvent<HTMLButtonElement>): void {
    // Must not bubble to the row's onClick below — that one navigates to the plugin's
    // detail page, and this button's job is only to expand/collapse text in place.
    event.stopPropagation();
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {/* Column order follows the prototype: Name · Users (30d) · Type · Last Updated ·
              Description. The stale plan doc placed the users column after Last Updated. */}
          <th>Name</th>
          <th>Users (30d)</th>
          <th>Type</th>
          <th>Last Updated</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {plugins.map((plugin) => {
          const rowKey = getRowKey(plugin);
          const isExpanded = expandedKeys.has(rowKey);
          // rowKey can be plugin.displayName ("Alpha Plugin (org/alpha)"), which contains
          // spaces — invalid in an HTML id. Collapse whitespace runs to a single hyphen.
          const descriptionId = `description-${rowKey.replace(/\s+/g, "-")}`;
          // The name is a real <a href> so right-click → "open in new tab" and
          // middle-click work (browser-native, no JS needed for those). Its
          // onClick stops propagation UNCONDITIONALLY, before the plain-left-click
          // guard: on a modified click the browser opens a new tab, so the row's
          // own onClick below must not also fire in-page SPA navigation. Making
          // this conditional would reintroduce that double-navigation.
          const handleNameClick = onSpaLinkClick(() => onSelectPlugin(plugin), { stopPropagation: true });
          return (
            // The row carries a mouse-only onClick for full-row clickability.
            // Deliberately left-click-only: new-tab/middle-click support is
            // confined to the name <a> below. Do not add preventDefault/guard
            // logic here — it would collide with handleNameClick's
            // unconditional stopPropagation.
            <tr
              key={rowKey}
              className={styles.row}
              onClick={() => onSelectPlugin(plugin)}
            >
              <td className={styles.nameCell}>
                <div className={styles.pluginName}>
                  <OfficialCheck plugin={plugin} size={15} />
                  <a
                    href={detailHref(plugin)}
                    className={styles.nameLink}
                    onClick={handleNameClick}
                  >
                    {plugin.pluginName}
                  </a>
                </div>
                <div className={styles.repoName}>{plugin.orgOrOwner}/{plugin.repoName}</div>
              </td>
              {/* "no usage yet" rather than the plan doc's "—": #266 AC 2 requires "a clear
                  'no usage yet' state rather than blank or broken", and a bare dash here would
                  also collide with the Last Updated column's own "—" — same glyph, two
                  different meanings, one row. Truthy check keeps a 0 out, as on the card. */}
              <td className={styles.usersCell}>
                {plugin.adoption?.activeUsers30d
                  ? <>{plugin.adoption.activeUsers30d}<SharedUsageNote sharedNameUsage={plugin.adoption.sharedNameUsage} extensionType={plugin.extensionType} /></>
                  : "no usage yet"}
              </td>
              {/* The Type cell carries New alongside the type badge — #620 asks for the
                  badge on cards, list view and detail, and this is list view's type slot.
                  No new COLUMN: the prototype has five, and a sixth for a transient state
                  would be a wide, mostly-empty column. */}
              <td>
                <div className={styles.typePills}>
                  {plugin.extensionType && (
                    <span className={`${styles.typeBadge} ${styles[EXTENSION_TYPE_CLASS[plugin.extensionType] ?? "typeBadgePlugin"]}`}>
                      {plugin.extensionType}
                    </span>
                  )}
                  <NewBadge plugin={plugin} />
                </div>
              </td>
              <td className={styles.updatedCell}>
                {(plugin.lastUpdatedAt && formatDate(plugin.lastUpdatedAt)) || "—"}
              </td>
              <DescriptionCell
                description={plugin.description}
                displayName={plugin.displayName}
                descriptionId={descriptionId}
                isExpanded={isExpanded}
                onToggle={(event) => toggleExpanded(rowKey, event)}
              />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
